/**
 * Utilitaires pour la gestion des statuts de dossiers (circuits, séjours, excursions, transferts)
 */

/**
 * Obtient les informations de statut d'un dossier basé sur les champs de clôture
 * @param {Object} dossier - L'objet dossier (circuit, séjour, excursion, transfert)
 * @returns {Object} - Objet contenant le libellé, la couleur et l'icône du statut
 */
export const getStatusInfo = (dossier) => {
    // Gérer les deux formats de noms de champs (snake_case et camelCase)
    const clotureDefinitive = dossier?.cloture_definitive || dossier?.clotureDefinitive;
    const preCloture = dossier?.pre_cloture || dossier?.preCloture;
    const cloture = dossier?.cloture;
    
    // Vérifier d'abord la clôture définitive (priorité la plus haute)
    if (clotureDefinitive === true) {
        return {
            label: 'Clôture définitive',
            shortLabel: '🔒 Clôture définitive',
            color: '#d32f2f', // Rouge
            bgColor: '#ffebee',
            icon: '🔒',
            status: 'closed'
        };
    } 
    // Ensuite vérifier la pré-clôture
    else if (preCloture === true) {
        return {
            label: 'Pré-clôturé',
            shortLabel: '⚠️ Pré-clôturé',
            color: '#ed6c02', // Orange
            bgColor: '#fff3e0',
            icon: '⚠️',
            status: 'pre-closed'
        };
    } 
    // Compatibilité avec l'ancien système
    else if (cloture === true) {
        return {
            label: 'Clôturé (ancien)',
            shortLabel: 'Clôturé',
            color: '#9c27b0', // Violet
            bgColor: '#f3e5f5',
            icon: '📋',
            status: 'legacy-closed'
        };
    }
    // Par défaut, le dossier est ouvert
    else {
        return {
            label: 'Ouvert',
            shortLabel: '📂 Ouvert',
            color: '#2e7d32', // Vert
            bgColor: '#e8f5e8',
            icon: '✅',
            status: 'open'
        };
    }
};

/**
 * Détermine si un dossier peut être modifié basé sur son statut
 * @param {Object} dossier - L'objet dossier 
 * @returns {boolean} - true si le dossier peut être modifié
 */
export const canModifyDossier = (dossier) => {
    // Gérer les deux formats de noms de champs
    const clotureDefinitive = dossier?.cloture_definitive || dossier?.clotureDefinitive;
    const preCloture = dossier?.pre_cloture || dossier?.preCloture;
    
    return !preCloture && !clotureDefinitive;
};

/**
 * Applique le statut à un dossier pour l'affichage dans les listes
 * @param {Object} dossier - L'objet dossier
 * @returns {Object} - Le dossier avec le champ statut mis à jour
 */
export const applyStatusToDossier = (dossier) => {
    const statusInfo = getStatusInfo(dossier);
    return {
        ...dossier,
        statut: statusInfo.shortLabel,
        statusInfo: statusInfo
    };
};

/**
 * Applique le statut à une liste de dossiers
 * @param {Array} dossiers - Tableau de dossiers
 * @returns {Array} - Tableau de dossiers avec statuts mis à jour
 */
export const applyStatusToList = (dossiers) => {
    return dossiers.map(dossier => applyStatusToDossier(dossier));
};

/**
 * Filtre les dossiers par statut
 * @param {Array} dossiers - Tableau de dossiers
 * @param {string} statusFilter - 'open', 'pre-closed', 'closed', 'all'
 * @returns {Array} - Dossiers filtrés
 */
export const filterDossiersByStatus = (dossiers, statusFilter) => {
    if (statusFilter === 'all') return dossiers;
    
    return dossiers.filter(dossier => {
        const statusInfo = getStatusInfo(dossier);
        return statusInfo.status === statusFilter;
    });
};

const statusHelpers = {
    getStatusInfo,
    canModifyDossier,
    applyStatusToDossier,
    applyStatusToList,
    filterDossiersByStatus
};

export default statusHelpers;
